import { useMemo, useState } from 'react';
import { Alert, Box } from '@nimbus-ds/components';
import { TFunction } from 'i18next';
import {
  Status as StatusEnum,
  Country,
  OrdersAutoCancelPaymentMethod,
} from '@tiendanube/common/src/enums';
import { Button, Card, DataList, InterfaceLabel } from '@tiendanube/components';
import { useNavegate } from 'App/hooks';
import { PAGO_NUBE_PROVIDER_NAME } from 'commons/constants';
import { useBoolean } from 'commons/hooks';
import {
  OrderMarkAsPaidModal,
  OrderMarkAsRefundedModal,
} from 'domains/Orders/components/OrderMarkAsPaidModal';
import { usePartialPayment } from 'domains/Orders/Orders/hooks';
import { getLatestPayment } from 'domains/Orders/Orders/utils/OrderDetailsPage/utils';
import { trackingOrderMarkAsPaidClick } from 'domains/Orders/tracking';
import OtherPaymentMethodModal from './OtherPaymentMethodModal';
import PaymentDetails from './PaymentDetails';
import PaymentDropdownButton from './PaymentDropdownButton';
import PaymentsInfoCardAmounts from './PaymentsInfoCardAmounts';
import { CUSTOM_GATEWAY } from '../../../constants';
import { useOrderIsEdited } from '../../../hooks';
import { useNuvemPagoRedirectResponse } from '../../../hooks/useNuvemPagoRedirect/useNuvemPagoRedirect';
import { OrderStatusType } from '../commons/useOrderStatus';

export interface GatewayData {
  gateway: string;
  gatewayMethod: string;
  gatewayName?: string;
}

interface NewCardPaymentProps {
  order: OrderStatusType;
  t: TFunction;
  country: Country;
  openGatewayLink: (gatewayLink: string) => void;
  showButton: boolean;
  headerLabel: InterfaceLabel;
  nuvemPagoRedirect: useNuvemPagoRedirectResponse;
  isMobile: boolean;
  openNuvemPago: () => void;
  isArchived: boolean;
  isCancelled: boolean;
  isMarkingAsPaid: boolean;
  paidOrder: (
    gateway?: string,
    gatewayMethod?: string,
    gatewayName?: string,
  ) => void;
  partiallyPaidOrder: (method: string, name?: string) => void;
  isMakingPartialPayment: boolean;
  handleMarkAsPaid: () => void;
}

function NewCardPayment({
  order,
  t,
  showButton,
  headerLabel,
  nuvemPagoRedirect,
  openGatewayLink,
  country,
  isMobile,
  openNuvemPago,
  isArchived,
  isCancelled,
  isMarkingAsPaid,
  paidOrder,
  partiallyPaidOrder,
  isMakingPartialPayment,
  handleMarkAsPaid,
}: NewCardPaymentProps): JSX.Element {
  const {
    id,
    number,
    payments,
    amounts,
    paymentsInfo,
    currency,
    products,
    fulfillmentOrders,
  } = order;

  const payment = getLatestPayment(order);
  const { goTo } = useNavegate();
  const isEdited = useOrderIsEdited();
  const [isModalOpen, onOpenModal, onCloseModal] = useBoolean(false);
  const [
    isConfirmationModalOpen,
    onOpenConfirmationModal,
    onCloseConfirmationModal,
  ] = useBoolean(false);
  const [gatewayData, setGatewayData] = useState<GatewayData | undefined>();
  const { partialPaymentButtonLabel, paymentMethodOptions } = usePartialPayment(
    {
      totalDiffPending: amounts.totalDiffPending,
      country: country as string,
      t,
      onOpenModal,
    },
  );

  // NOTE: For now we are assuming the first payment for the partially paid feature.
  const currentGateway = payment.gateway;
  const status = payment.status;
  const methodCode = payment.methodCode;
  const paymentMethod = payment.method;

  const handlePartialPayment = (
    gateway: string,
    gatewayMethod: string,
    gatewayName?: string,
  ) => {
    setGatewayData({ gateway, gatewayMethod, gatewayName });
    onOpenConfirmationModal();
  };

  const handleConfirmPartialPayment = (gatewayData?: GatewayData) => {
    if (!gatewayData) {
      throw new Error('Gateway information is not set');
    }
    handlePayOrder(gatewayData);
    setGatewayData(undefined);
    onCloseConfirmationModal();
  };

  const handleOtherPaymentMethodSubmit = (method: string, name?: string) => {
    handlePayOrder({
      gateway: CUSTOM_GATEWAY,
      gatewayMethod: method,
      gatewayName: name,
    });
    onCloseModal();
  };

  const handlePayOrder = (gatewayData: GatewayData) => {
    status === StatusEnum.PENDING
      ? (paidOrder(
          gatewayData.gateway,
          gatewayData.gatewayMethod,
          gatewayData.gatewayName,
        ),
        trackingOrderMarkAsPaidClick(gatewayData.gatewayMethod))
      : (partiallyPaidOrder(gatewayData.gatewayMethod, gatewayData.gatewayName),
        trackingOrderMarkAsPaidClick(gatewayData.gatewayMethod));
  };

  const handleNuvemPagoRedirect = () =>
    goTo(nuvemPagoRedirect.refundReceiptURL(id));

  const actionableOrder = !isCancelled && !isArchived;

  const totalProducts = products.reduce(
    (total, product) => total + product.quantity,
    0,
  );

  const originalTotalDiff = order.amounts.originalTotal
    ? Math.abs(order.amounts.originalTotal - order.amounts.total)
    : 0;

  const isStaleCVU = useMemo(
    () =>
      originalTotalDiff > 0 &&
      order.payment.status === StatusEnum.PENDING &&
      order.payment.gateway === PAGO_NUBE_PROVIDER_NAME &&
      order.payment.methodCode ===
        OrdersAutoCancelPaymentMethod.WIRE_TRANSFER &&
      country === Country.AR &&
      isEdited,
    [originalTotalDiff, order.payment, country, isEdited],
  );

  return (
    <Card title={t('cardPayment.title')} headerLabel={headerLabel}>
      <DataList ruled={false} spacing="tight">
        <PaymentsInfoCardAmounts
          amounts={amounts}
          paymentsInfo={paymentsInfo}
          currency={currency}
          paymentMethod={paymentMethod}
          fulfillmentOrders={fulfillmentOrders}
          totalProducts={totalProducts}
          isEdited={isEdited}
          charges={payment.charges}
          gateway={currentGateway}
          paymentCreationDate={payment.createdAt}
          paymentStatus={status}
        />
        <>
          <DataList.Row id="bottom">
            <DataList.Cell width="fill">
              <PaymentDetails
                payments={payments}
                currency={currency}
                isNuvemPagoPayment={nuvemPagoRedirect.isNuvemPagoPayment()}
                onNuvemPagoRedirect={handleNuvemPagoRedirect}
                openGatewayLink={openGatewayLink}
                country={country}
                isMobile={isMobile}
                openNuvemPago={openNuvemPago}
              />
            </DataList.Cell>
          </DataList.Row>
          {isStaleCVU && (
            <Box paddingX="4" paddingY="2">
              <Alert appearance="warning">{t('cardPayment.staleCVU')}</Alert>
            </Box>
          )}
          {actionableOrder &&
            (isEdited && amounts.total !== amounts.paidByCustomer ? (
              <DataList.Row id="footerButton">
                <DataList.Cell width="fill">
                  <PaymentDropdownButton
                    orderGatewayMethod={methodCode}
                    methodOptions={paymentMethodOptions}
                    label={partialPaymentButtonLabel}
                    isMakingPayment={isMakingPartialPayment || isMarkingAsPaid}
                    onPartialPayment={handlePartialPayment}
                  />
                </DataList.Cell>
              </DataList.Row>
            ) : (
              actionableOrder &&
              showButton && (
                <DataList.Row id="footerButton">
                  <DataList.Cell width="fill">
                    <Button
                      appearance="primary"
                      onClick={handleMarkAsPaid}
                      spinner={isMarkingAsPaid}
                      disabled={isMarkingAsPaid}
                    >
                      {`${t(`cardPayment.action.${status}`)}`}
                    </Button>
                  </DataList.Cell>
                </DataList.Row>
              )
            ))}
        </>
      </DataList>
      <OtherPaymentMethodModal
        isOpen={isModalOpen}
        isCharge={amounts.totalDiffPending > 0}
        onClose={onCloseModal}
        onSubmit={handleOtherPaymentMethodSubmit}
        orderId={number}
        total={amounts.totalDiffPending}
      />
      {amounts.totalDiffPending > 0 ? (
        <OrderMarkAsPaidModal
          orderId={number}
          show={isConfirmationModalOpen}
          total={amounts.totalDiffPending}
          onClose={onCloseConfirmationModal}
          gatewayData={gatewayData}
          onConfirm={handleConfirmPartialPayment}
        />
      ) : (
        <OrderMarkAsRefundedModal
          orderId={number}
          show={isConfirmationModalOpen}
          total={-amounts.totalDiffPending}
          onClose={onCloseConfirmationModal}
          gatewayData={gatewayData}
          onConfirm={handleConfirmPartialPayment}
        />
      )}
    </Card>
  );
}

export default NewCardPayment;
